import classNames from 'classnames';
import { HashLink as Link } from 'react-router-hash-link';
import ButtonLoading from '../buttonLoading/buttonLoading';
import type { NormalButtonProps } from './types';
import { useNormalButton } from './useNormalButton';
import './normalButton.scss';

const EMPTY_STYLE = {};

/**
 * Text-labeled button rendered either as a router link or a native button. Composes the filled, text or outline variant class with the bold, saving and disabled modifiers, takes onClick from useNormalButton and neutralizes it while isSaving or disabled, optionally rebinds activation to mousedown, and overlays ButtonLoading during a save.
 *
 * @param props - Button name and id, variant and state flags, link target, optional icon, styling overrides and the click handler.
 * @returns The rendered link or button element.
 */
const NormalButton = (props: NormalButtonProps) => {
  const {
    id = undefined,
    isFilled = false,
    isText = false,
    isSaving = false,
    disabled = false,
    type = 'button',
    className = undefined,
    style = EMPTY_STYLE,
    hasIcon = false,
    iconComponent = undefined,
    isBold = false,
    isLinkButton = false,
    link = '',
    target = undefined,
    titleSaving = undefined,
    contentClassName = undefined,
    isUseMousedownEvent = false,
    buttonName,
  } = props;
  const { onClick } = useNormalButton(props);

  return isLinkButton ? (
    <Link
      id={id}
      className={classNames(
        'link-button',
        isFilled
          ? classNames('filled-btn', isSaving ? '--is-saving' : '')
          : isText
            ? classNames('text-btn', isBold ? '--bold' : '')
            : 'outline-btn',
        disabled ? '--disabled' : '',
        className,
      )}
      type={type}
      style={style}
      to={link}
      target={target}
    >
      <div className="flex justify-center">
        <span className={classNames('name', isSaving ? '--transparent' : '')}>{buttonName}</span>

        {hasIcon && iconComponent}
      </div>
    </Link>
  ) : (
    <button
      id={id}
      className={classNames(
        isFilled
          ? classNames('filled-btn', isSaving ? '--is-saving' : '')
          : isText
            ? classNames('text-btn', isBold ? '--bold' : '')
            : classNames('outline-btn', isSaving ? '--is-saving' : ''),
        disabled ? '--disabled' : '',
        className,
      )}
      type={type as any}
      onClick={isUseMousedownEvent || isSaving || disabled ? () => {} : onClick}
      style={style}
      {...(isUseMousedownEvent
        ? {
            onMouseDown: isSaving || disabled ? () => {} : onClick,
          }
        : {})}
    >
      <div className={classNames('flex justify-center', contentClassName)}>
        <span className={classNames('name', isSaving ? '--transparent' : '')}>{buttonName}</span>

        {hasIcon && iconComponent}
      </div>

      {isSaving && (
        <ButtonLoading
          title={titleSaving}
          className={className}
          isFilled={isFilled}
          shouldDisplayLoadingCursor
        />
      )}
    </button>
  );
};

export default NormalButton;
